import { useState, useCallback, useMemo } from 'react';
import { useData, UseDataOptions, UseDataResult } from './useData';
import { 
  PaginationParams, 
  PaginatedResult, 
  createPaginationParams 
} from '@/utils/pagination/paginationUtils';

export interface UsePaginatedDataOptions<T> extends Omit<UseDataOptions<PaginatedResult<T>>, 'fetchFn'> {
  // Function that fetches paginated data
  fetchFn: (params: PaginationParams) => Promise<PaginatedResult<T>>;
  
  // Initial pagination parameters
  initialParams?: Partial<PaginationParams>;
}

export interface UsePaginatedDataResult<T> extends Omit<UseDataResult<PaginatedResult<T>>, 'data'> {
  // The paginated data
  data: T[];
  
  // Pagination metadata
  pagination: {
    currentPage: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  
  // Pagination control functions
  goToPage: (page: number) => void;
  nextPage: () => void;
  previousPage: () => void;
  setPageSize: (size: number) => void;
  
  // Current pagination parameters
  params: PaginationParams;
}

/**
 * A hook for fetching paginated data with built-in pagination controls
 * 
 * @param options - Configuration options for the hook
 * @returns Object containing paginated data, pagination metadata, and control functions
 */
export function usePaginatedData<T>(
  options: UsePaginatedDataOptions<T>
): UsePaginatedDataResult<T> {
  const { 
    fetchFn, 
    initialParams = {}, 
    cacheKey,
    ...restOptions 
  } = options;

  // Create pagination parameters
  const [params, setParams] = useState<PaginationParams>(
    createPaginationParams(
      initialParams.page || 1,
      initialParams.pageSize || 10
    )
  );

  // Create a cache key that includes pagination parameters
  const paginatedCacheKey = cacheKey 
    ? `${cacheKey}:page:${params.page}:size:${params.pageSize}` 
    : undefined;

  // Create a fetch function that includes pagination parameters
  const paginatedFetchFn = useCallback(
    () => fetchFn(params),
    [fetchFn, params]
  );

  // Use the base useData hook with our paginated fetch function
  const result = useData<PaginatedResult<T>>({
    ...restOptions,
    fetchFn: paginatedFetchFn,
    cacheKey: paginatedCacheKey,
    dependencies: [params, ...(restOptions.dependencies || [])]
  });

  // Extract pagination metadata from the result
  const pagination = useMemo(() => {
    if (!result.data) {
      return {
        currentPage: params.page,
        pageSize: params.pageSize,
        totalItems: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: params.page > 1
      };
    }
    
    return result.data.metadata;
  }, [result.data, params]);

  // Pagination control functions
  const goToPage = useCallback((page: number) => {
    setParams(prev => ({
      ...prev,
      page: Math.max(1, page)
    }));
  }, []);

  const nextPage = useCallback(() => {
    if (pagination.hasNextPage) {
      goToPage(pagination.currentPage + 1);
    }
  }, [pagination, goToPage]);

  const previousPage = useCallback(() => {
    if (pagination.hasPreviousPage) {
      goToPage(pagination.currentPage - 1);
    }
  }, [pagination, goToPage]);

  const setPageSize = useCallback((size: number) => {
    setParams(prev => ({
      ...prev,
      pageSize: Math.max(1, Math.min(100, size)),
      // Reset to page 1 when changing page size
      page: 1
    }));
  }, []);

  return {
    ...result,
    data: result.data?.data || [],
    pagination,
    goToPage,
    nextPage,
    previousPage,
    setPageSize,
    params
  };
} 